// Product Taxonomy Schema and Database Operations
import { db } from './firebase';
import { collection, doc, setDoc, getDoc, getDocs, updateDoc, serverTimestamp } from 'firebase/firestore';

// AI PROTECTION: All AI systems must identify themselves
export const validateAIAccess = (source, operation) => {
  const aiSources = ['ai', 'smart-quote', 'openai', 'gpt', 'chatgpt', 'claude', 'assistant'];
  const isAI = aiSources.some(ai => source?.toLowerCase().includes(ai));
  
  if (isAI) {
    console.error(`AI_PROTECTION: ${source} blocked from ${operation} on taxonomy levels 1-4`);
    throw new Error(`AI systems (${source}) are not permitted to modify Product Level 1-4 taxonomy structure. Manual setup only.`);
  }
  return true;
};

// COMPLETELY EMPTY - NO DEFAULT DATA DURING MANUAL SETUP
export const defaultTaxonomy = {
  metadata: {
    version: "2.0.0",
    lastUpdated: null,
    createdBy: "manual_setup",
    note: "Empty taxonomy for manual setup - no AI data"
  },
  products: {
    // COMPLETELY EMPTY - manually add your items through the interface
  }
};

// Question templates for conversational UI
export const questionTemplates = {
  initial: [
    "Hi! Let's create something fabulous. What piece are we designing today?",
    "Good morning! What beautiful item shall we bring to life?",
    "Hello there! I'm excited to help design your next piece. What are we creating?"
  ],
  style: [
    "Great choice! Now, what style of {product} are we talking about?",
    "Perfect! I love {product}s. What specific type did you have in mind?",
    "Excellent! {product}s are wonderful. Which style appeals to you?"
  ],
  dimensions: [
    "Now for the fun part - let's talk size! I'll need some measurements.",
    "Time to get specific! What dimensions are we working with?",
    "Let's make sure this fits perfectly. Can you give me the measurements?"
  ],
  materials: [
    "What material speaks to you for this {product}?",
    "Let's choose the perfect material. What catches your eye?",
    "Materials can make or break a piece. What's calling to you?"
  ],
  features: [
    "Any special features you'd like to include?",
    "Let's add some personality! What features should we consider?",
    "What extra touches would make this piece perfect?"
  ]
};

// Initialize taxonomy in Firestore - DISABLED DURING SETUP
export const initializeTaxonomy = async () => {
  console.log('initializeTaxonomy() DISABLED - manual setup in progress');
  
  // Return minimal empty structure instead of defaultTaxonomy
  const emptyTaxonomy = {
    metadata: {
      version: "2.0.0",
      lastUpdated: new Date().toISOString(),
      createdBy: "manual_setup",
      note: "Empty taxonomy for manual setup"
    },
    products: {}
  };
  
  try {
    const taxonomyRef = doc(db, 'settings', 'productTaxonomy');
    const taxonomyDoc = await getDoc(taxonomyRef);
    
    if (!taxonomyDoc.exists()) {
      await setDoc(taxonomyRef, emptyTaxonomy);
      console.log('Empty taxonomy structure created for manual setup');
      return emptyTaxonomy;
    }
    
    return taxonomyDoc.data();
  } catch (error) {
    console.error('Error creating empty taxonomy:', error);
    return emptyTaxonomy;
  }
};

// Get current taxonomy - NO AUTO-INITIALIZATION DURING SETUP
export const getTaxonomy = async () => {
  try {
    const taxonomyRef = doc(db, 'settings', 'productTaxonomy');
    const taxonomyDoc = await getDoc(taxonomyRef);
    
    if (taxonomyDoc.exists()) {
      return taxonomyDoc.data();
    }
    
    // Return empty structure instead of auto-initializing
    console.log('No taxonomy found - returning empty structure for manual setup');
    return {
      metadata: {
        version: "2.0.0",
        lastUpdated: new Date().toISOString(),
        createdBy: "manual_setup"
      },
      products: {}
    };
  } catch (error) {
    console.error('Error getting taxonomy:', error);
    throw error;
  }
};

// Update taxonomy
export const updateTaxonomy = async (newTaxonomy, options = {}) => {
  // AI PROTECTION: Block AI from updating taxonomy structure
  if (options.isAI || options.source === 'ai' || options.source === 'smart-quote') {
    console.warn('AI_PROTECTION: AI cannot update taxonomy structure');
    throw new Error('AI systems are not allowed to modify taxonomy structure');
  }
  
  try {
    const taxonomyRef = doc(db, 'settings', 'productTaxonomy');
    
    // Create update data with proper timestamp handling
    const updateData = {
      ...newTaxonomy,
      metadata: {
        ...newTaxonomy.metadata,
        lastUpdated: new Date().toISOString() // Use ISO string instead of serverTimestamp for this operation
      }
    };
    
    await setDoc(taxonomyRef, updateData);
    console.log('Taxonomy updated successfully');
    return updateData;
  } catch (error) {
    console.error('Error updating taxonomy:', error);
    throw error;
  }
};

// Check usage of taxonomy item in projects
export const checkTaxonomyUsage = async (productKey, categoryKey = null, subcategoryKey = null, featuresKey = null, additionalsKey = null) => {
  try {
    const { collection: firestoreCollection, getDocs, query, where } = await import('firebase/firestore');
    
    // Check in projects (Smart Quote data)
    const projectsSnapshot = await getDocs(firestoreCollection(db, 'projects'));
    const usageResults = {
      projects: [],
      totalCount: 0
    };

    // Get the actual item label for precise matching
    const taxonomy = await getTaxonomy();
    let itemLabel = '';
    
    console.log('Checking usage for:', { productKey, categoryKey, subcategoryKey, featuresKey, additionalsKey });
    
    if (additionalsKey && featuresKey && subcategoryKey && categoryKey && productKey) {
      const product = taxonomy.products?.[productKey];
      const category = product?.[categoryKey];
      const subcategory = category?.[subcategoryKey];
      const feature = subcategory?.[featuresKey];
      const additional = feature?.[additionalsKey];
      itemLabel = additional?.label || '';
      console.log('Additional path:', { product: !!product, category: !!category, subcategory: !!subcategory, feature: !!feature, additional: !!additional, label: itemLabel });
    } else if (featuresKey && subcategoryKey && categoryKey && productKey) {
      const product = taxonomy.products?.[productKey];
      const category = product?.[categoryKey];
      const subcategory = category?.[subcategoryKey];
      const feature = subcategory?.[featuresKey];
      itemLabel = feature?.label || '';
      console.log('Feature path:', { product: !!product, category: !!category, subcategory: !!subcategory, feature: !!feature, label: itemLabel });
    } else if (subcategoryKey && categoryKey && productKey) {
      const product = taxonomy.products?.[productKey];
      const category = product?.[categoryKey];
      const subcategory = category?.[subcategoryKey];
      itemLabel = subcategory?.label || '';
      console.log('Subcategory path:', { product: !!product, category: !!category, subcategory: !!subcategory, label: itemLabel });
    } else if (categoryKey && productKey) {
      const product = taxonomy.products?.[productKey];
      const category = product?.[categoryKey];
      itemLabel = category?.label || '';
      console.log('Category path:', { product: !!product, category: !!category, label: itemLabel });
    } else if (productKey) {
      const product = taxonomy.products?.[productKey];
      itemLabel = product?.label || '';
      console.log('Product path:', { product: !!product, label: itemLabel });
    }

    if (!itemLabel) {
      return usageResults; // Item not found, return empty results
    }

    projectsSnapshot.docs.forEach(doc => {
      const project = { id: doc.id, ...doc.data() };
      let isUsed = false;
      let usageContext = '';

      // Check if project description or name contains the exact item label
      const searchText = `${project.name || ''} ${project.description || ''} ${project.projectDescription || ''}`.toLowerCase();
      const itemLabelLower = itemLabel.toLowerCase();
      
      // Only flag as used if the exact item label is mentioned
      if (searchText.includes(itemLabelLower)) {
        isUsed = true;
        usageContext = `Contains "${itemLabel}"`;
      }

      if (isUsed) {
        usageResults.projects.push({
          id: project.id,
          name: project.name,
          customerName: project.customer?.name || 'Unknown',
          status: project.status || 'planning',
          context: usageContext
        });
      }
    });

    usageResults.totalCount = usageResults.projects.length;
    return usageResults;
  } catch (error) {
    console.error('Error checking taxonomy usage:', error);
    return { projects: [], totalCount: 0 };
  }
};

// Archive taxonomy item (soft delete)
export const archiveTaxonomyItem = async (productKey, categoryKey = null, subcategoryKey = null, featuresKey = null, additionalsKey = null, options = {}) => {
  // AI PROTECTION: Block AI from archiving taxonomy items
  if (options.isAI || options.source === 'ai' || options.source === 'smart-quote') {
    console.warn('AI_PROTECTION: AI cannot archive taxonomy items');
    throw new Error('AI systems are not allowed to modify taxonomy structure');
  }
  
  try {
    const taxonomy = await getTaxonomy();
    
    // Navigate to the item and mark as archived
    console.log('Archiving item:', { productKey, categoryKey, subcategoryKey, featuresKey, additionalsKey });
    
    if (additionalsKey && featuresKey && subcategoryKey && categoryKey && productKey) {
      // Archive additional
      const additional = taxonomy.products?.[productKey]?.[categoryKey]?.[subcategoryKey]?.[featuresKey]?.[additionalsKey];
      if (additional) {
        additional.archived = true;
        additional.archivedAt = new Date().toISOString();
        console.log('Archived additional:', additional.label);
      } else {
        console.warn('Additional not found for archiving');
      }
    } else if (featuresKey && subcategoryKey && categoryKey && productKey) {
      // Archive feature
      const feature = taxonomy.products?.[productKey]?.[categoryKey]?.[subcategoryKey]?.[featuresKey];
      if (feature) {
        feature.archived = true;
        feature.archivedAt = new Date().toISOString();
        console.log('Archived feature:', feature.label);
      } else {
        console.warn('Feature not found for archiving');
      }
    } else if (subcategoryKey && categoryKey && productKey) {
      // Archive subcategory
      const subcategory = taxonomy.products?.[productKey]?.[categoryKey]?.[subcategoryKey];
      if (subcategory) {
        subcategory.archived = true;
        subcategory.archivedAt = new Date().toISOString();
        console.log('Archived subcategory:', subcategory.label);
      } else {
        console.warn('Subcategory not found for archiving');
      }
    } else if (categoryKey && productKey) {
      // Archive category
      const category = taxonomy.products?.[productKey]?.[categoryKey];
      if (category) {
        category.archived = true;
        category.archivedAt = new Date().toISOString();
        console.log('Archived category:', category.label);
      } else {
        console.warn('Category not found for archiving');
      }
    } else if (productKey) {
      // Archive product
      const product = taxonomy.products?.[productKey];
      if (product) {
        product.archived = true;
        product.archivedAt = new Date().toISOString();
        console.log('Archived product:', product.label);
      } else {
        console.warn('Product not found for archiving');
      }
    }

    return await updateTaxonomy(taxonomy);
  } catch (error) {
    console.error('Error archiving taxonomy item:', error);
    throw error;
  }
};

// Permanently delete taxonomy item (use with caution)
export const deleteTaxonomyItem = async (productKey, categoryKey = null, subcategoryKey = null, featuresKey = null, additionalsKey = null, options = {}) => {
  // AI PROTECTION: Block AI from deleting taxonomy items
  if (options.isAI || options.source === 'ai' || options.source === 'smart-quote') {
    console.warn('AI_PROTECTION: AI cannot delete taxonomy items');
    throw new Error('AI systems are not allowed to modify taxonomy structure');
  }
  
  try {
    const taxonomy = await getTaxonomy();
    
    // Navigate to the item and delete it
    console.log('Deleting item:', { productKey, categoryKey, subcategoryKey, featuresKey, additionalsKey });
    
    if (additionalsKey && featuresKey && subcategoryKey && categoryKey && productKey) {
      // Delete additional
      const feature = taxonomy.products?.[productKey]?.[categoryKey]?.[subcategoryKey]?.[featuresKey];
      if (feature && feature[additionalsKey]) {
        delete feature[additionalsKey];
        console.log('Deleted additional:', additionalsKey);
      } else {
        console.warn('Additional not found for deletion');
      }
    } else if (featuresKey && subcategoryKey && categoryKey && productKey) {
      // Delete feature
      const subcategory = taxonomy.products?.[productKey]?.[categoryKey]?.[subcategoryKey];
      if (subcategory && subcategory[featuresKey]) {
        delete subcategory[featuresKey];
        console.log('Deleted feature:', featuresKey);
      } else {
        console.warn('Feature not found for deletion');
      }
    } else if (subcategoryKey && categoryKey && productKey) {
      // Delete subcategory
      const category = taxonomy.products?.[productKey]?.[categoryKey];
      if (category && category[subcategoryKey]) {
        delete category[subcategoryKey];
        console.log('Deleted subcategory:', subcategoryKey);
      } else {
        console.warn('Subcategory not found for deletion');
      }
    } else if (categoryKey && productKey) {
      // Delete category
      const product = taxonomy.products?.[productKey];
      if (product && product[categoryKey]) {
        delete product[categoryKey];
        console.log('Deleted category:', categoryKey);
      } else {
        console.warn('Category not found for deletion');
      }
    } else if (productKey) {
      // Delete product
      if (taxonomy.products && taxonomy.products[productKey]) {
        delete taxonomy.products[productKey];
        console.log('Deleted product:', productKey);
      } else {
        console.warn('Product not found for deletion');
      }
    }

    return await updateTaxonomy(taxonomy);
  } catch (error) {
    console.error('Error deleting taxonomy item:', error);
    throw error;
  }
};

// Helper functions for navigation
export const getProductsByCategory = (taxonomy, categoryKey) => {
  const category = taxonomy.categories[categoryKey];
  if (!category || !category.subcategories) return [];
  
  const products = [];
  Object.entries(category.subcategories).forEach(([subKey, subcategory]) => {
    if (subcategory.products) {
      Object.entries(subcategory.products).forEach(([productKey, product]) => {
        products.push({
          key: productKey,
          label: product.label,
          category: categoryKey,
          subcategory: subKey,
          ...product
        });
      });
    }
  });
  
  return products;
};

export const getStylesByProduct = (taxonomy, categoryKey, subcategoryKey, productKey) => {
  const product = taxonomy.categories[categoryKey]?.subcategories[subcategoryKey]?.products[productKey];
  if (!product || !product.styles) return [];
  
  return Object.entries(product.styles).map(([styleKey, style]) => ({
    key: styleKey,
    label: style.label,
    ...style
  }));
};

// Add new item to taxonomy
export const addToTaxonomy = async (path, newItem, options = {}) => {
  // AI PROTECTION: Block AI from adding to top 5 levels
  if (options.isAI || options.source === 'ai' || options.source === 'smart-quote') {
    const pathDepth = path.split('.').filter(p => p !== 'products').length;
    if (pathDepth <= 5) {
      console.warn('AI_PROTECTION: AI cannot modify taxonomy levels 1-5');
      throw new Error('AI systems are not allowed to modify Product Level 1-5 taxonomy structure');
    }
  }

  try {
    const taxonomy = await getTaxonomy();
    
    // Navigate to the correct path and add the item
    let current = taxonomy;
    const pathArray = path.split('.');
    
    for (let i = 0; i < pathArray.length - 1; i++) {
      if (!current[pathArray[i]]) {
        current[pathArray[i]] = {};
      }
      current = current[pathArray[i]];
    }
    
    const lastKey = pathArray[pathArray.length - 1];
    if (!current[lastKey]) {
      current[lastKey] = {};
    }
    
    // Add the new item with validation
    const itemKey = newItem.key || newItem.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    
    // Ensure we have a valid key
    if (!itemKey || itemKey.trim() === '') {
      throw new Error('Item key cannot be empty');
    }
    
    // Clean the newItem to remove any empty or invalid fields
    const cleanItem = { ...newItem };
    delete cleanItem.key; // Remove the key from the item itself
    delete cleanItem.type; // Remove the type field - it's only for UI, not stored data
    
    // Remove any empty string fields that could cause Firebase issues
    Object.keys(cleanItem).forEach(key => {
      if (cleanItem[key] === '' || cleanItem[key] === null || cleanItem[key] === undefined) {
        delete cleanItem[key];
      }
    });
    
    current[lastKey][itemKey] = cleanItem;
    
    // Update in database
    await updateTaxonomy(taxonomy);
    
    return taxonomy;
  } catch (error) {
    console.error('Error adding to taxonomy:', error);
    throw error;
  }
};